import { ref, watch, watchEffect, computed, toRef, nextTick } from 'vue'
import { useValidation } from '@/composables/utils/useValidation'
import { useMediaUpload } from '@/composables/utils/network/useMediaUpload'
import { useArtistDirectBookingSteps } from './useArtistDirectBookingSteps'
import { useDirectBookingWelcome } from './useDirectBookingWelcome'
import { useDirectBookingAuth } from './useDirectBookingAuth'
import { useDirectBookingArtistUpdate } from './useDirectBookingArtistUpdate'
import { useDirectBookingMakeOffer } from './useDirectBookingMakeOffer'
import { useDirectBookingEventData } from './useDirectBookingEventData'

const authed = ref(false)
const social = ref(false)
const event = ref(null)
const load = ref(false)
const validation = ref(null)
const ctaClicked = ref(false)

const profilePicIndx = ref(0)
const editImage = ref(null)
const croppedImage = ref(null)

const opportunityInfo = ref(null)
const user = ref(null)
const artists = ref([])
const userData = ref({
    id: null,
    name: '',
    welcome: {
        email: ''
    },
    login: {
        password: ''
    },
    makeOffer: {
        counter_offer: '',
        counter_offer_duration: '',
        start_avail: '',
        extra_requirements: '',
        start_avail_time: '12:00',
        start_avail_format: 'AM'
    },
    register: {
        first_name: '',
        last_name: '',
        password: ''
    },
    artistInfo: {
        id: null,
        band_name: '',
        home_location: '',
        travel_radius: '',
        lat: null,
        lon: null,
        keyword: ''
    },
    images: [],
    videos: [],
    audios: [],
    bioPayout: {
        long_biography: '',
        min_payout: ''
    },
    socials: {
        Instagram: '',
        Facebook: '',
        Tiktok: '',
        Youtube: '',
        Spotify: '',
        Mixcloud: '',
        Soundcloud: ''
    },
    socialInfo: {
        slug: '',
        web_site: ''
    }
})

export const useArtistDirectBookingInternal = () => {
    return {
        social
    }
}
export const useArtistDirectBooking = (props, { emit }) => {
    const id = toRef(props, 'eventId')
    const showModal = toRef(props, 'showModal')
    const stepId = toRef(props, 'stepId')
    const isAuthed = toRef(props, 'authed')
    const userId = toRef(props, 'userId')
    const artistPages = toRef(props, 'artistPages')

    const { isValid } = useValidation()
    const {
        register,
        login
        //     fetchUser
    } = useDirectBookingAuth(userData, artists, load)
    const newArtist = ref(true)
    const { autocomplete, updateArtist, updateSocials } = useDirectBookingArtistUpdate(userData, newArtist, load)
    const { makeOffer } = useDirectBookingMakeOffer(userData, event, load)
    const { fetchOpportunityInfo } = useDirectBookingEventData()
    const { updateProfileMedia, updateProfileAvatar, updateArtistAudio } = useMediaUpload()
    const isEditImgAvatar = computed(() => {
        return userData.value.images.indexOf(editImage.value) === profilePicIndx.value
    })
    const { steps, currentStep, updateStep } = useArtistDirectBookingSteps(
        isAuthed,
        event,
        opportunityInfo,
        editImage,
        social,
        userData,
        validation,
        profilePicIndx,
        isEditImgAvatar,
        croppedImage,
        artists,
        load
    )
    const { isUser } = useDirectBookingWelcome(currentStep, userData, load, toggleDirectBooking)

    ;(async () => {
        if (!id.value && !event.value) {
            throw new Error('Missing Event/Opportunity ID')
        }
    })()

    watchEffect(() => {
        if (artistPages.value) {
            artists.value = artistPages.value
        }
    })
    const show = ref(showModal.value)
    watch(
        () => userId.value,
        (id) => {
            if (id) {
                userData.value.id = id
            }
        },
        { immediate: true }
    )
    watch(
        () => stepId.value,
        (id) => {
            if (id) {
                authed.value = true
                updateStep(id)
            }
        },
        { immediate: true }
    )
    watch(
        () => id.value,
        async (eventId) => {
            event.value = eventId
            opportunityInfo.value = await fetchOpportunityInfo(event.value)
        },
        { immediate: true }
    )
    watch(
        () => showModal.value,
        (showBaseModal) => {
            show.value = showBaseModal
        }
    )
    watch(
        () => show.value,
        (showBaseModal) => {
            emit('update:show-modal', showBaseModal)
            if (!showBaseModal) {
                if (userData.value.id && !isAuthed.value) window.location.reload()
            }
        }
    )
    watch(
        () => isAuthed.value,
        (isAuthed) => {
            authed.value = isAuthed
        },
        { immediate: true }
    )
    // watch(
    //     () => authed.value,
    //     async (isAuthed) => {
    //         if (isAuthed) {
    //             user.value = await fetchUser()
    //             _populateUserData(user.value)
    //         }
    //     },
    //     { immediate: true }
    // )
    function _populateUserData(user) {
        userData.value.id = user.user.id
        Object.keys(userData.value.artistInfo).forEach((k) => {
            if (user.info[k]) userData.value.artistInfo[k] = user.info[k]
            if (user.info['lng']) userData.value.artistInfo['lon'] = user.info['lng']
        })
        Object.keys(userData.value.bioPayout).forEach((k) => {
            if (user.info[k]) userData.value.bioPayout[k] = user.info[k]
        })
        Object.keys(userData.value.socials).forEach((k) => {
            if (user.socials[k]) userData.value.socials[k] = user.socials[k]
        })
        Object.keys(userData.value.socialInfo).forEach((k) => {
            if (user.info[k]) userData.value.socialInfo[k] = user.info[k]
        })
        userData.value.audios = user.audios
        userData.value.images = user.medias.filter((m) => m.type === 'image')
        userData.value.videos = user.medias.filter((m) => m.type === 'video')

    }

    function toggleDirectBooking(state) {
        if (state) {
            show.value = state
        } else {
            _clearAllData()
            updateStep('offer-details')
            show.value = state
        }
    }

    function _clearAllData() {
        const clearKeys = Object.keys(userData.value).filter((key) => key !== 'id' && key !== 'name')
        clearKeys.forEach((key) => _clearData(key))
        userData.value.audios = []
        userData.value.videos = []
        userData.value.images = []
    }

    async function onContinue(action) {
        if (!action) action = currentStep.value.actions.continue

        switch (currentStep.value.identifier) {
            case 'offer-details':
                _offerDetailsNext(action)
                break
            case 'choose-artist':
                _chooseArtistNext(action)
                break
            case 'welcome':
                await _welcomeNext(validation.value, action)
                break
            case 'login':
                await _loginNext(validation.value, action)
                break
            case 'make-offer':
                await _makeOfferNext(validation.value, action)
                break
            case 'register':
                await _registerNext(validation.value, action)
                break
            case 'artist-info':
                await _artistInfoNext(validation.value, action)
                break
            case 'upload-media':
                _uploadMediaNext(action)
                break
            case 'organize-media':
                await _organizeMediaNext(action)
                break
            case 'offer-sent':
                _offerSentNext(action)
                break
            case 'bio-payout':
                await _bioPayoutNext(validation.value, action)
                break
            case 'socials':
                await _socialsNext(validation.value, action)
                break
            case 'final':
            case 'authed-offer-sent':
                toggleDirectBooking(false)
                break
            case 'edit-photo':
                _editPhotoNext(action)
                break
        }
    }
    function onBack(action) {
        switch (currentStep.value.identifier) {
            case 'offer-details':
                _offerDetailsBack()
                break
            case 'choose-artist':
                _chooseArtistBack(action)
                break
            case 'welcome':
                _welcomeBack(action)
                break
            case 'login':
                _loginBack()
                break
            case 'make-offer':
                _makeOfferBack(action)
                break
            case 'register':
                _registerBack(action)
                break
            case 'artist-info':
                _artistInfoBack(action)
                break
            case 'upload-media':
                _uploadMediaBack(action)
                updateStep('artist-info')
                break
            case 'organize-media':
                _organizeMediaBack(action)
                break
            case 'offer-sent':
                _offerSentBack()
                break
            case 'bio-payout':
                _bioPayoutBack(action)
                break
            case 'socials':
                _socialsBack(action)
                break
            case 'final':
                _finalBack(action)
                break
            case 'edit-photo':
                updateStep(action)
                break
        }
    }

    /**
     * @param {String} clear clear keys from step definition.
     */
    function onClear(clear) {
        if (Array.isArray(clear) && clear.every((c) => ['audios', 'images', 'videos'].includes(c))) {
            clear.forEach((c) => (userData.value[c] = []))
        } else if (Array.isArray(clear)) {
            clear.forEach((c) => _clearData(c))
        } else {
            _clearData(clear)
        }
    }
    function _clearData(clear) {
        Object.keys(userData.value[clear]).forEach((key) => {
            if (clear === 'makeOffer' && key === 'start_avail_time') {
                userData.value[clear][key] = '12:00'
            } else if (clear === 'makeOffer' && key === 'start_avail_format') {
                userData.value[clear][key] = 'AM'
            } else {
                userData.value[clear][key] = ''
            }
        })
    }

    function _offerDetailsNext(action) {
        if (authed.value) {
            updateStep('choose-artist')
            // switch (role.value) {
            //     case 'artist':
            //         updateStep(action.authed.artist)
            //         break
            //     case 'venue':
            //         updateStep(action.authed.venue)
            //         break
            //     case null:
            //         updateStep(action.authed.artist)
            //         break
            //     default:
            //         updateStep(action.authed.guest)
            //    }
        } else {
            updateStep(action.guest)
        }
    }
    function _offerDetailsBack() {
        return
    }

    function _chooseArtistNext(action) {
        if (user.value) {
            updateStep(action)
        } else {
            return
        }
    }
    function _chooseArtistBack(action) {
        updateStep(action)
    }

    /**
     * @param {Object} v validation object
     * @param {String|Object} action continue action string or object from step definition
     */
    async function _welcomeNext(v, action) {
        if (!isValid(v)) return

        if (await isUser(v)) {
            updateStep(action.authed)
        } else {
            updateStep(action.guest)
        }
    }

    /**
     * @param {String|Object} action back action string or object from step definition
     */
    function _welcomeBack(action) {
        updateStep(action)
    }

    async function _loginNext(v, action) {
        if (!isValid(v)) return
        if (await login(v)) {
            authed.value = true
            updateStep(action)
        } else {
            authed.value = false
        }
    }
    function _loginBack() {
        return
    }

    const missingBooking = computed(() => {
        if (
            user.value?.info &&
            user.value.info.band_name &&
            user.value.info.home_location &&
            user.value.info.lat &&
            user.value.info.lng &&
            user.value.info.travel_radius &&
            user.value.info.keyword
        ) {
            return false
        }
        return true
    })
    const missingMedias = computed(() => {
        if (user.value?.audios?.length && user.value?.medias?.length) {
            return false
        }
        return true
    })
    async function _makeOfferNext(v, action) {
        if (!isValid(v)) return

        await nextTick()

        if (social.value) {
            if (await makeOffer()) {
                updateStep(action.authed.social)
            }
        } else if (user.value && !missingBooking.value && !missingMedias.value) {
            if (await makeOffer()) {
                updateStep(action.authed.complete)
            }
        } else if (user.value && missingBooking.value && !missingMedias.value) {
            updateStep(action.authed.missingBooking)
        } else if (user.value && !missingBooking.value && missingMedias.value) {
            updateStep(action.authed.missingMedia)
        } else if (user.value && missingBooking.value && missingMedias.value) {
            updateStep(action.authed.missingBooking)
        } else {
            updateStep(action.guest)
        }
    }
    function _makeOfferBack(action) {
        authed.value === false ? updateStep(action.guest) : updateStep(action.authed)
        return
    }

    async function _registerNext(v, action) {
        if (!isValid(v)) return
        if (await register(v)) {
            updateStep(action)
        }
    }
    function _registerBack(action) {
        updateStep(action)
    }

    async function _artistInfoNext(v, action) {
        if (!isValid(v)) return
        if (await updateArtist(v, userData.value.artistInfo)) {
            // NOTE: setting authed.value here instead of login, so that
            // we can check if user is already logged in or not going back a step
            authed.value = true
            newArtist.value = false
            updateStep(action)
        }
    }
    function _artistInfoBack(action) {
        if (authed.value === true && !user.value.artistBooking) {
            updateStep(action.authed)
        } else {
            // hidden logic is also applied in the markup
            return
        }
    }

    function _uploadMediaNext(action) {
        updateStep(action)
    }
    function _uploadMediaBack(action) {
        updateStep(action)
    }

    async function _organizeMediaNext(action) {
        if (
            (await updateProfileAvatar(
                userData.value.images,
                profilePicIndx.value,
                userData.value.artistInfo.id,
                load
            )) &&
            (await updateProfileMedia(
                userData.value.images.concat(userData.value.videos),
                profilePicIndx.value,
                userData.value.artistInfo.id,
                load
            )) &&
            (await updateArtistAudio(userData.value.audios, userData.value.artistInfo.id, load))
        ) {
            if (await makeOffer()) {
                updateStep(action)
            } else {
                return
            }
        } else {
            return
        }
    }
    function _organizeMediaBack(action) {
        updateStep(action)
    }

    function _offerSentNext(action) {
        updateStep(action)
    }
    function _offerSentBack() {
        return
    }

    async function _bioPayoutNext(v, action) {
        if (!isValid(v)) return
        if (await updateArtist(v, userData.value.bioPayout)) {
            updateStep(action)
        }
    }
    function _bioPayoutBack(action) {
        updateStep(action)
    }

    async function _socialsNext(v, action) {
        if (!isValid(v)) return
        if (
            (await updateArtist(v, userData.value.socialInfo)) && //
            (await updateSocials(v, userData.value.artistInfo.id))
        ) {
            updateStep(action)
        }
    }
    function _socialsBack(action) {
        updateStep(action)
    }

    function _finalBack(action) {
        updateStep(action)
    }

    function _editPhotoNext(action) {
        userData.value.images.splice(profilePicIndx.value, 1)
        userData.value.images.splice(profilePicIndx.value, 0, croppedImage.value)
        updateStep(action)
    }

    function toggleProfileImage(image) {
        const indx = userData.value.images.indexOf(image)
        profilePicIndx.value = indx
    }

    function chosenArtist(artist) {
        if (artist) {
            newArtist.value = false
            user.value = artist._booking
            _populateUserData(artist._booking)
        } else {
            newArtist.value = true
            user.value = {}
            _clearAllData()
        }
    }
    return {
        authed,
        autocomplete,
        load,
        show,
        social,
        validation,
        ctaClicked,
        currentStep,
        steps,
        userData,
        opportunityInfo,
        editImage,
        toggleProfileImage,
        chosenArtist,

        toggleDirectBooking,
        profilePicIndx,
        isEditImgAvatar,
        croppedImage,

        onContinue,
        onClear,
        onBack,
        updateStep
    }
}
